import React from 'react';
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { Venue, BookingAvailabilitySlot } from '@/types';
import { isDateBlockedBySchedule, getAvailableTimeSlotsForDate } from '@/lib/scheduling';

interface DateTimePickerProps {
  selectedVenueId: string | null;
  selectedVenue: Venue | undefined;
  selectedServiceId: string | null;
  selectedDate: Date | null;
  selectedTime: string | null;
  visibleMonth: Date;
  availabilitySlots: BookingAvailabilitySlot[];
  hasAvailabilityData: boolean;
  isLoadingAvailability: boolean;
  isFindingNextAvailable: boolean;
  nextAvailableMessage: string | null;
  requiredDuration: number;
  onDateSelect: (date: Date) => void;
  onTimeSelect: (time: string) => void;
  onMonthChange: (offset: number) => void;
  onFindNextAvailable: () => void;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  selectedVenueId,
  selectedVenue,
  selectedServiceId,
  selectedDate,
  selectedTime,
  visibleMonth,
  availabilitySlots,
  hasAvailabilityData,
  isLoadingAvailability,
  isFindingNextAvailable,
  nextAvailableMessage,
  requiredDuration,
  onDateSelect,
  onTimeSelect,
  onMonthChange,
  onFindNextAvailable
}) => {
  if (!selectedVenueId) {
    return (
      <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 text-slate-500 text-sm text-center">
        Select a location to view availability.
      </div>
    );
  }

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const minBookableDate = new Date(today);
  const monthStart = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
  const monthEnd = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);
  const minMonthStart = new Date(minBookableDate.getFullYear(), minBookableDate.getMonth(), 1);
  const canGoPrevMonth = monthStart.getTime() > minMonthStart.getTime();
  const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const leadingEmptyDays = monthStart.getDay();
  const dates = Array.from(
    { length: monthEnd.getDate() },
    (_, i) => new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), i + 1)
  );

  const generatedSlots = selectedDate
    ? getAvailableTimeSlotsForDate(selectedDate, selectedVenue?.schedule, {
        requiredDurationMinutes: requiredDuration
      })
    : [];
  const availabilityByTime = new Map(availabilitySlots.map((s) => [s.time, s]));
  const availableSlots = selectedDate
    ? hasAvailabilityData
      ? availabilitySlots.map((s) => s.time)
      : generatedSlots
    : [];

  const isSlotConflict = (time: string) => {
    const slot = availabilityByTime.get(time);
    if (slot) return !slot.available;
    return false;
  };

  const hasSelectableSlot = availableSlots.some((t) => !isSlotConflict(t));
  const showNoSlots =
    Boolean(selectedDate) && !isLoadingAvailability && availableSlots.length === 0;
  const showFullyBooked =
    Boolean(selectedDate) &&
    !isLoadingAvailability &&
    availableSlots.length > 0 &&
    !hasSelectableSlot;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      <div className="md:col-span-2">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-bold text-slate-700 flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-teal-600" /> Available Dates
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onMonthChange(-1)}
              disabled={!canGoPrevMonth}
              className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-teal-400 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="text-sm font-bold text-slate-800 min-w-[140px] text-center">
              {visibleMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            </div>
            <button
              onClick={() => onMonthChange(1)}
              className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-teal-400 flex items-center justify-center"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-2 mb-2">
          {weekdayLabels.map((label) => (
            <div key={label} className="text-[10px] font-bold text-slate-400 text-center uppercase">
              {label}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: leadingEmptyDays }).map((_, idx) => (
            <div key={`empty-${idx}`} />
          ))}
          {dates.map((d) => {
            const isSelected = selectedDate?.toDateString() === d.toDateString();
            const { blocked, reason } = isDateBlockedBySchedule(
              d,
              selectedVenue?.schedule,
              'Closed on this day'
            );
            const beforeBookingWindow = d.getTime() < minBookableDate.getTime();
            const isToday = d.toDateString() === now.toDateString();
            const hasSlotsToday =
              isToday && selectedVenue
                ? getAvailableTimeSlotsForDate(d, selectedVenue.schedule, {
                    requiredDurationMinutes: requiredDuration
                  }).length > 0
                : true;
            const noSlotsLeftToday = !blocked && isToday && !hasSlotsToday;
            const isUnavailable = blocked || beforeBookingWindow || noSlotsLeftToday;
            return (
              <button
                key={d.toISOString()}
                disabled={isUnavailable}
                title={
                  beforeBookingWindow
                    ? 'Bookings cannot be in the past.'
                    : blocked
                      ? reason
                      : noSlotsLeftToday
                        ? 'No remaining slots today.'
                        : reason
                }
                onClick={() => onDateSelect(d)}
                className={`py-3 px-1 rounded-xl border text-center transition-all relative ${isSelected ? 'bg-teal-600 text-white border-teal-600 shadow-lg' : 'bg-white hover:border-teal-400 shadow-sm'} ${isUnavailable ? 'opacity-40 cursor-not-allowed bg-slate-50' : ''}`}
              >
                <div className="text-[10px] font-bold uppercase opacity-75">{d.getDate()}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 h-fit shadow-inner">
        <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-teal-600" /> Select Time
        </h3>
        {!selectedServiceId ? (
          <p className="text-slate-500 text-sm text-center py-10">Select a service to see times.</p>
        ) : !selectedDate ? (
          <p className="text-slate-500 text-sm text-center py-10">Select a date to see times.</p>
        ) : isLoadingAvailability ? (
          <p className="text-slate-500 text-sm text-center py-10">Loading availability...</p>
        ) : showNoSlots ? (
          <p className="text-slate-400 text-sm italic text-center py-10">
            No slots available for this day.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {availableSlots.map((time) => {
              const slot = availabilityByTime.get(time);
              const isConflict = isSlotConflict(time);
              return (
                <button
                  key={time}
                  disabled={isConflict}
                  onClick={() => onTimeSelect(time)}
                  className={`py-2 px-3 text-xs rounded-lg border transition-all font-black ${selectedTime === time ? 'bg-teal-600 text-white border-teal-600' : isConflict ? 'bg-red-50 text-red-300 border-red-100 cursor-not-allowed line-through' : 'bg-white text-slate-700 border-slate-200 hover:border-teal-400 shadow-sm'}`}
                >
                  {time}
                  {slot && slot.capacity > 1 ? ` (${slot.remaining} left)` : ''}
                </button>
              );
            })}
          </div>
        )}
        {(showNoSlots || showFullyBooked) && (
          <div className="mt-4 bg-white border border-slate-200 rounded-xl p-3 text-center">
            <p className="text-xs text-slate-500">
              {showFullyBooked
                ? 'This day is fully booked.'
                : 'No appointments available on this day.'}
            </p>
            <button
              onClick={onFindNextAvailable}
              disabled={isFindingNextAvailable}
              className="mt-2 inline-flex items-center justify-center px-3 py-1.5 text-xs font-bold rounded-lg border border-teal-200 text-teal-700 bg-teal-50 hover:bg-teal-100 disabled:opacity-50"
            >
              {isFindingNextAvailable ? 'Searching...' : 'Find next available'}
            </button>
          </div>
        )}
        {nextAvailableMessage && (
          <p className="mt-3 text-[11px] text-slate-500 text-center">{nextAvailableMessage}</p>
        )}
      </div>
    </div>
  );
};
